import { useEffect, useState } from 'react';
import { useLocale } from '../i18n/locale-context';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Textarea } from './ui/textarea';
import { Spinner } from './ui/spinner';
import {
  CHURN_REASON_CHANGING_CARD,
  CHURN_REASON_REQUIRING_TEXT,
  CHURN_REASON_TEXT_MAX,
  churnReasonOptions,
  churnRetentionHint,
  type ChurnReasonKind,
} from '../lib/churn-reasons';
import { COMPANY, HAS_TELEGRAM } from '../lib/company';
import { pluralRu } from '../lib/format';

/**
 * «Почему отключаете продление?» — the one question asked before the two
 * gestures that end a subscription (Igor, 2026-09-21: «Причину отписки
 * реализуем»).
 *
 * ## Why it is an interception and not a follow-up
 *
 * Asking AFTER the cancel is asking someone who has already left, on a screen
 * they have no reason to still be looking at. Asking BEFORE costs the buyer one
 * tap and gives the answer a denominator — every cancellation is now either a
 * reason or an explicit "did not say", not a silence we cannot interpret.
 *
 * It is not a retention wall. The ✕ (and Esc, and the overlay) closes with
 * nothing changed and the checkbox stays checked (the panel never mutated it —
 * see the call site). Nothing here discounts or delays; the pause is an offer
 * the subscriber may take or ignore.
 *
 * ## The rules it follows (owner's checkout rules)
 *
 *  - **The confirm button is never `disabled`.** A disabled button fires no DOM
 *    events, which is exactly how 128 `checkout_failed{empty_number}` taps
 *    stayed invisible for a month — and it never says WHY it is dead. Pressed
 *    with nothing chosen, it shows ONE inline message instead.
 *  - **One message per field**, never a stack.
 *  - **No `scrollIntoView`.** Nothing here scrolls anything: the list is short,
 *    the body is the sheet's own scroller and the buttons are a pinned footer,
 *    so they are on screen without moving the panel under the reader.
 *  - The free-text box opens for EVERY choice, not just «Другое». Someone
 *    who picks «Дорого» and wants to say how much is the most useful answer on
 *    the sheet, and gating the box behind one option throws that away.
 *
 * ## What changed on 2026-09-21 (Igor)
 *
 *  - **«Другое» is the FIRST row**, not the last — see `churn-reasons.ts`.
 *  - **Choosing it makes the free text REQUIRED.** «Другое» with nothing after
 *    it is the one answer that tells us less than no answer at all: it occupies
 *    a cancellation in the breakdown and explains none of it. The button still
 *    is not `disabled`; pressing it with the box empty shows the message.
 *
 * ## What changed on 2026-10-10 (Igor: too much text, and «год за 4 млн
 * ## вместо 8 — не понимаю смысл»)
 *
 *  - Title «Отключить автопродление?», a small «Почему?» over the list, and
 *    each reason in two or three words.
 *  - ONE value line for everyone — «По вашим поискам за месяц пришло N лотов»
 *    — when the caller has the count (`matchCount`); none → no line. The
 *    year-price line is gone from here (it stays on /plans).
 *  - **«Пауза на 1 месяц — потом продолжится по старой цене» is the MAIN,
 *    filled button**, under every reason (and before one is picked) whenever
 *    a pause is available. «Отключить продление» is the grey secondary one.
 *  - «Оставить продление» is gone: the ✕ top right — the same close every
 *    other sheet has — plus Esc and the overlay close it with nothing changed.
 */

/** «лот / лота / лотов»; invariant in Uzbek and Kazakh. */
function lotsWord(n: number, locale: string): string {
  if (locale === 'uz') return 'lot';
  if (locale === 'kk') return 'лот';
  if (locale === 'es') return n === 1 ? 'lote' : 'lotes';
  return pluralRu(n, 'лот', 'лота', 'лотов');
}

/** `fetchNewMatchCount` is capped server-side at this — a returned 100 means
 *  «at least 100», so it is printed as «100+», never as an exact figure. */
const MATCH_COUNT_CAP = 100;

export function ChurnReasonModal({
  open,
  kind,
  pending = false,
  onCancel,
  onConfirm,
  matchCount = null,
  onPause,
  pausePending = false,
  pauseDates = null,
}: {
  open: boolean;
  /** Which gesture is being intercepted — decides the title, the grey
   *  button's wording and whether the three card-only codes are offered. */
  kind: ChurnReasonKind;
  /** The mutation is in flight — spinner on the confirm button. The button
   *  still is not `disabled`; a second press while pending is harmless (the
   *  conditional UPDATE in `setAutoRenew` makes the loser a no-op). */
  pending?: boolean;
  /** The ✕, Esc, the overlay — close with NOTHING changed. */
  onCancel: () => void;
  /** A reason was chosen and the confirm button pressed. `reasonText` is
   *  trimmed and may be empty. */
  onConfirm: (reason: string, reasonText: string) => void;
  /**
   * Lots the subscriber's searches brought in the last 30 days that are still
   * open (`fetchNewMatchCount`, capped at 100). Null or 0 → no value line: a
   * zero is not an argument for staying, and a guess is not a figure.
   */
  matchCount?: number | null;
  /**
   * «Пауза на 1 месяц» (2026-10-09). Passed only when a pause is available to
   * this subscriber (once per subscription, auto-renew sheet only). Since
   * 10.10 it is the sheet's MAIN button, under every reason; `reason` is null
   * when it is pressed before one is picked. Absent → never shown.
   */
  onPause?: (reason: string | null, reasonText: string) => void;
  /** The pause request is in flight — spinner on that button. */
  pausePending?: boolean;
  /**
   * The window the pause would set, already formatted for the reader: Pro
   * closes on `from` (the period end), the subscription carries on — and is
   * charged — on `to`. Null → the dateless note.
   */
  pauseDates?: { from: string; to: string } | null;
}) {
  const { t, locale } = useLocale();
  const [reason, setReason] = useState<string | null>(null);
  const [text, setText] = useState('');
  /** Which ONE message is on screen — never a stack, never two at once. */
  const [error, setError] = useState<'reason' | 'details' | null>(null);
  const options = churnReasonOptions(kind);
  /** «Другое» explains nothing by itself, so its free text is mandatory. */
  const textRequired = reason === CHURN_REASON_REQUIRING_TEXT;
  /* The Telegram line for the two reasons a person can fix; suppressed on a
     telegram-off deployment — KZ has no such account to point at. */
  const hint = churnRetentionHint(reason);
  const retention = hint && (!hint.telegram || HAS_TELEGRAM) ? hint : null;
  const showPause = Boolean(onPause);
  const valueLine =
    matchCount && matchCount > 0
      ? t('churn.value', {
          n: matchCount >= MATCH_COUNT_CAP ? `${MATCH_COUNT_CAP}+` : String(matchCount),
          word: lotsWord(matchCount, locale),
        })
      : null;
  /*
   * Fresh every time it opens. Without this, closing the sheet and reopening it
   * a week later would show the answer from the abandoned attempt already
   * selected — and a pre-selected radio is an answer nobody gave.
   */
  useEffect(() => {
    if (!open) return;
    setReason(null);
    setText('');
    setError(null);
  }, [open, kind]);

  function submit() {
    if (!reason) {
      setError('reason');
      return;
    }
    const trimmed = text.trim();
    if (reason === CHURN_REASON_REQUIRING_TEXT && !trimmed) {
      setError('details');
      return;
    }
    onConfirm(reason, trimmed);
  }

  /* «Меняю карту» (Igor, 10.10) does not delete anything — the caller opens the
     card-attach sheet — so the button says what will happen, and there is no
     free-text box: nothing is sent to the server for this answer. */
  const changingCard = kind === 'card' && reason === CHURN_REASON_CHANGING_CARD;
  const title = kind === 'card' ? t('churn.title.card') : t('churn.title.autoRenew');
  const confirmLabel = changingCard
    ? t('billing.autoRenew.changeCard')
    : kind === 'card'
      ? t('churn.confirm.card')
      : t('churn.confirm.autoRenew');

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      {/* A flex column that does NOT scroll, `p-0`: the reason list scrolls in
          the body below and the buttons are the panel's last row — the same
          shape the card sheet and the счёт sheet have had since 2026-09-20. */}
      <DialogContent
        mobileVariant="sheet"
        className="mx-auto flex max-h-[88vh] max-w-md flex-col overflow-hidden border-edge !bg-bone-surface p-0"
        /* OPEN QUIETLY — Radix focuses the first tabbable descendant on mount;
           a sheet that opens with an answer under the focus ring reads as one
           that has pre-answered for you. */
        onOpenAutoFocus={(e) => e.preventDefault()}
        aria-describedby={undefined}
        data-testid="churn-reason-modal"
      >
        {/* The ✕ every sheet on the site has (card sheet, support form): same
            size, same place, a 44 px hit box via the pseudo-element. */}
        <button
          type="button"
          onClick={onCancel}
          aria-label={t('support.close')}
          className="absolute right-3 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-md text-[15px] text-ink transition-colors before:absolute before:-inset-2 before:content-[''] hover:bg-bone md:top-3"
        >
          ✕
        </button>

        <div className="min-h-0 flex-auto overflow-y-auto overscroll-contain px-5 pb-3 pt-4 md:px-6 md:pt-5">
          <DialogTitle className="pr-10 text-[18px] font-bold leading-snug text-ink">
            {title}
          </DialogTitle>

          {valueLine && (
            <p
              className="mt-2 text-[14px] font-semibold leading-snug text-brand"
              data-testid="churn-value"
            >
              {valueLine}
            </p>
          )}

          <p className="mt-4 text-[12px] font-bold uppercase tracking-[0.05em] text-ink-muted">
            {t('churn.why')}
          </p>
          <RadioGroup
            value={reason ?? ''}
            onValueChange={(v) => {
              setReason(v);
              // One message, and it goes out the moment it stops being true.
              setError(null);
            }}
            className="mt-1 gap-0"
            aria-label={title}
          >
            {options.map((o) => (
              <label
                key={o.code}
                htmlFor={`churn-${o.code}`}
                /* The whole row is the target, not the 18px dot. */
                className="flex cursor-pointer items-center gap-3 rounded-md px-1 py-2 transition-colors hover:bg-bone"
              >
                <RadioGroupItem
                  id={`churn-${o.code}`}
                  value={o.code}
                  className="border-edge data-[state=checked]:border-brand"
                />
                <span className="text-[14px] leading-snug text-ink">{t(o.key)}</span>
              </label>
            ))}
          </RadioGroup>

          {/* The box appears once there is an answer to add to — enabled for
              EVERY reason then, and mandatory under «Другое». */}
          {reason && !changingCard && (
            <>
              <label
                htmlFor="churn-text"
                className="mt-3 block text-[12.5px] font-semibold text-ink"
              >
                {textRequired ? t('churn.detailsRequired') : t('churn.detailsLabel')}
              </label>
              <Textarea
                id="churn-text"
                value={text}
                maxLength={CHURN_REASON_TEXT_MAX}
                onChange={(e) => {
                  setText(e.target.value);
                  if (e.target.value.trim()) setError(null);
                }}
                className="mt-1.5 min-h-[64px] rounded-md border-[1.5px] border-edge bg-bone text-[16px] text-ink focus-visible:border-brand"
              />
            </>
          )}
        </div>

        {/* PINNED FOOTER — the panel's last flex row, never `position:fixed`,
            so it rides inside the sheet's own height cap instead of under the
            iOS keyboard the textarea above opens. */}
        <div className="shrink-0 border-t border-edge bg-bone-surface px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 md:px-6">
          {retention && (
            <p className="mb-2 text-[12.5px] leading-snug text-ink-soft">
              {retention.telegram ? (
                <a
                  href={COMPANY.telegramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-brand underline underline-offset-2 hover:text-brand-mid"
                >
                  {t(retention.key)}
                </a>
              ) : (
                t(retention.key)
              )}
            </p>
          )}
          {error && (
            <p role="alert" className="mb-2 text-[12.5px] font-medium text-status-closing-text">
              {error === 'details' ? t('churn.detailsRequired') : t('churn.pickReason')}
            </p>
          )}
          {showPause && (
            <div className="mb-2">
              <button
                type="button"
                onClick={() => onPause?.(reason, text.trim())}
                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-center text-[14.5px] font-bold leading-snug text-white transition-colors hover:bg-brand-mid"
              >
                {pausePending ? <Spinner size="sm" /> : null}
                {t('churn.pause')}
              </button>
              <p className="mt-1 text-center text-[12px] leading-snug text-ink-muted">
                {pauseDates ? t('churn.pauseNote', pauseDates) : t('churn.pauseNoteNoDate')}
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            className="inline-flex h-[44px] w-full items-center justify-center gap-2 rounded-md border border-edge bg-bone px-4 text-[13.5px] font-semibold text-ink-soft transition-colors hover:bg-edge-light hover:text-ink"
          >
            {pending ? <Spinner size="sm" /> : null}
            {confirmLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
